import api, {
  buildCompanyScopeHeaders,
} from "./api";


const RECEIVING_BASE_URL =
  "/supply-chain/receiving/purchase-orders";


function requireString(value, fieldName) {
  if (typeof value !== "string" || !value.trim()) {
    throw new TypeError(`${fieldName} is required.`);
  }

  return value.trim();
}


function normalizeScope({
  companyUuid,
  activityUuid,
}) {
  return {
    companyUuid: requireString(
      companyUuid,
      "companyUuid",
    ),
    activityUuid: requireString(
      activityUuid,
      "activityUuid",
    ),
  };
}


function buildScopeHeaders(scope) {
  const {
    companyUuid,
    activityUuid,
  } = normalizeScope(scope);

  return {
    ...buildCompanyScopeHeaders(companyUuid),
    "X-Activity-UUID": activityUuid,
  };
}


export function extractReceivingApiError(
  error,
  fallback = "Unable to process the receiving request.",
) {
  const payload = error?.response?.data;

  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
}


export async function getReceivingContext({
  purchaseOrderReference,
  companyUuid,
  activityUuid,
  signal,
}) {
  const reference = requireString(
    purchaseOrderReference,
    "purchaseOrderReference",
  );

  const response = await api.get(
    `${RECEIVING_BASE_URL}/${encodeURIComponent(reference)}`,
    {
      headers: buildScopeHeaders({
        companyUuid,
        activityUuid,
      }),
      signal,
    },
  );

  return response?.data?.receiving ?? null;
}


export async function executeReceivingOperation({
  purchaseOrderReference,
  operationKey,
  lineReference,
  quantity,
  reason = null,
  metadata = {},
  companyUuid,
  activityUuid,
  signal,
}) {
  const reference = requireString(
    purchaseOrderReference,
    "purchaseOrderReference",
  );

  const normalizedOperationKey = requireString(
    operationKey,
    "operationKey",
  );

  const normalizedLineReference = requireString(
    lineReference,
    "lineReference",
  );

  if (
    quantity === null
    || quantity === undefined
    || String(quantity).trim() === ""
  ) {
    throw new TypeError("quantity is required.");
  }

  const payload = {
    operation_key: normalizedOperationKey,
    line_reference: normalizedLineReference,
    quantity: String(quantity).trim(),
    reason:
      typeof reason === "string" && reason.trim()
        ? reason.trim()
        : null,
    metadata:
      metadata && typeof metadata === "object"
        ? metadata
        : {},
  };

  const response = await api.post(
    `${RECEIVING_BASE_URL}/${encodeURIComponent(
      reference,
    )}/operations`,
    payload,
    {
      headers: buildScopeHeaders({
        companyUuid,
        activityUuid,
      }),
      signal,
    },
  );

  return response?.data?.receiving ?? null;
}


export default Object.freeze({
  getReceivingContext,
  executeReceivingOperation,
  extractReceivingApiError,
});
